import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { StatCard } from '@/components/StatCard'
import { DashboardChart } from '@/components/DashboardChart'
import {
  activePrograms,
  describeMyAffiliateEmptyState,
  fetchMyAffiliatePaymentsAll,
  fetchMyAffiliatePrograms,
  formatMyAffiliateCurrency,
  manualPrograms,
} from '@/lib/my-affiliate'
import { MyAffiliateEmptyState } from '@/pages/SouAfiliadoPage/MyAffiliateEmptyState'
import type { MyAffiliatePaymentItem, MyAffiliateProgram } from '@/types/my-affiliate'
import styles from './SouAfiliadoPage.module.css'

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function buildMonthlyChart(payments: MyAffiliatePaymentItem[]) {
  const map = new Map<string, { vendas: number; comissoes: number }>()
  for (const p of payments) {
    const date = new Date(p.paid_at)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const entry = map.get(key) ?? { vendas: 0, comissoes: 0 }
    entry.vendas += p.payment_amount
    entry.comissoes += p.commission_amount
    map.set(key, entry)
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([key, values]) => {
      const month = parseInt(key.split('-')[1], 10) - 1
      return {
        mes: `${MONTH_LABELS[month]}/${key.split('-')[0].slice(2)}`,
        ...values,
      }
    })
}

export function SouAfiliadoDashboardPage() {
  const [programs, setPrograms] = useState<MyAffiliateProgram[]>([])
  const [emptyMessage, setEmptyMessage] = useState(
    describeMyAffiliateEmptyState(undefined, null, [])
  )
  const [globalSales, setGlobalSales] = useState(0)
  const [globalCommission, setGlobalCommission] = useState(0)
  const [payments, setPayments] = useState<MyAffiliatePaymentItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyAffiliatePrograms()
      setPrograms(res.programs)
      setEmptyMessage(
        describeMyAffiliateEmptyState(res.login_context, res.profile, res.programs)
      )
      setGlobalSales(res.global.total_sales)
      setGlobalCommission(res.global.total_commission)

      const paymentsRes = await fetchMyAffiliatePaymentsAll({ per_page: 100 })
      setPayments(paymentsRes.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar seus dados de afiliado.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const active = useMemo(() => activePrograms(programs), [programs])
  const manual = useMemo(() => manualPrograms(programs), [programs])
  const totalBalance = useMemo(
    () => manual.reduce((sum, p) => sum + p.balance, 0),
    [manual]
  )
  const totalIndications = useMemo(
    () => programs.reduce((acc, p) => acc + p.metrics.payments_count, 0),
    [programs]
  )
  const ticketMedio = useMemo(
    () => (totalIndications > 0 ? globalSales / totalIndications : 0),
    [globalSales, totalIndications]
  )
  const monthlyData = useMemo(() => buildMonthlyChart(payments), [payments])
  const topPrograms = useMemo(
    () => [...programs].sort((a, b) => b.metrics.total_sales - a.metrics.total_sales).slice(0, 5),
    [programs]
  )

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  if (programs.length === 0) {
    return (
      <MyAffiliateEmptyState icon="mdi:account-star-outline" message={emptyMessage} />
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.statsGrid}>
        <StatCard
          title="Programas Ativos"
          value={active.length}
          icon="🏢"
          subtitle={`${programs.length} no total`}
        />
        <StatCard
          title="Indicações Pagas"
          value={totalIndications.toLocaleString('pt-BR')}
          icon="🤝"
          subtitle="Vendas atribuídas a você"
        />
        <StatCard
          title="Vendas Geradas"
          value={formatMyAffiliateCurrency(globalSales)}
          icon="💰"
        />
        <StatCard
          title="Comissões"
          value={formatMyAffiliateCurrency(globalCommission)}
          icon="💸"
        />
      </div>

      <div className={styles.statsGridSecondary}>
        <StatCard
          title="Ticket Médio"
          value={formatMyAffiliateCurrency(ticketMedio)}
          icon="🎫"
        />
        {manual.length > 0 ? (
          <StatCard
            title="Saldo Manual"
            value={formatMyAffiliateCurrency(totalBalance)}
            icon="👛"
            subtitle={`${manual.length} programa(s) · ver aba Saldo`}
          />
        ) : (
          <StatCard
            title="Pagamento"
            value="Automático (Doey)"
            icon="⚡"
            subtitle="Comissão via split no PIX"
          />
        )}
      </div>

      <div className={styles.mainContent}>
        <div className={styles.chartSection}>
          <DashboardChart title="Suas vendas e comissões">
            {monthlyData.length === 0 ? (
              <div className={styles.emptyChart}>
                <Icon icon="mdi:chart-line" />
                <span>Sem vendas pagas para exibir no período</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,22,54,0.1)" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6c757d' }} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6c757d' }}
                    tickFormatter={(v) => `R$${(Number(v) / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value) =>
                      formatMyAffiliateCurrency(typeof value === 'number' ? value : 0)
                    }
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid rgba(59,22,54,0.2)',
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="vendas"
                    name="Vendas"
                    stroke="#5e17eb"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="comissoes"
                    name="Comissões"
                    stroke="#d4006b"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </DashboardChart>
        </div>

        <div className={styles.programList}>
          <h3 className={styles.programListTitle}>
            <Icon icon="mdi:trophy" />
            Seus programas
          </h3>
          {topPrograms.length === 0 ? (
            <div className={styles.emptyBlock}>
              <span>Nenhum programa com vendas ainda</span>
            </div>
          ) : (
            <ul className={styles.programItems}>
              {topPrograms.map((program) => (
                <li key={program.affiliate_id} className={styles.programItem}>
                  <span className={styles.programName}>{program.institution.name}</span>
                  <span className={styles.programMeta}>
                    {program.metrics.payments_count} vendas ·{' '}
                    {formatMyAffiliateCurrency(program.metrics.total_sales)}
                  </span>
                  <span
                    className={program.is_active ? styles.badgeActive : styles.badgeInactive}
                  >
                    {program.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p style={{ marginTop: 'var(--spacing-md)', fontSize: 'var(--font-size-xs)' }}>
            <Link to="/sou-afiliado/links">Ver campanhas e copiar links</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
